import React, { useState, useEffect } from 'react';
import {
  editPenIcon,
  trashGridIcon,
} from '../assets/superAdminAssets';
import SideNavigation from '../components/SideNavigation';
import Header from '../components/Header';
import AddPlanDrawer from '../components/AddPlanDrawer';
import DataTable from '../components/DataTable';
import { useAppContext } from '../contexts/AppContext';
import { Constants } from '../constants';
import { listSubscriptionPlans, toggleSubscriptionPlanStatus } from '../services/subscriptionPlan.service';
import moment from 'moment';
import { getBoolean } from '../helpers/index.helper';
import { formatDollars } from '../helpers/number.helper';

const Plans = () => {
  const { showLoader, hideLoader, showToaster, hideToaster, isLoading } = useAppContext();
  const [showAddPlanModal, setShowAddPlanModal] = useState(false);
  const [showAddPlanModalAnimation, setShowAddPlanModalAnimation] = useState(false);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [showStatusConfirmPopup, setShowStatusConfirmPopup] = useState(false);
  const [statusConfirmPlan, setStatusConfirmPlan] = useState(null);
  const [planId, setPlanId] = useState(0);
  const [plansData, setPlansData] = useState([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    document.title = 'Loan Mantra - Subscription Plans';
    showLoader();
  }, []);

  // Handle add plan modal animation
  useEffect(() => {
    if (showAddPlanModal) {
      // Small delay to ensure the initial state is rendered before adding show class
      // This allows the browser to paint the initial state (translateX(100%)) first
      const timer = setTimeout(() => {
        setShowAddPlanModalAnimation(true);
      }, 50);
      return () => clearTimeout(timer);
    } else {
      // Remove show class immediately when closing to trigger exit animation
      setShowAddPlanModalAnimation(false);
    }
  }, [showAddPlanModal]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchSubscriptionPlans(page, rowsPerPage, searchText);
    }, 500);
    return () => clearTimeout(timeout);
  }, [page, rowsPerPage, searchText]);

  useEffect(() => {
    setPage(1);
  }, [searchText]);

  async function fetchSubscriptionPlans(pageIndex, pageSize, search) {
    try {
      showLoader();
      const res = await listSubscriptionPlans(pageIndex, pageSize, search || '');
      if (res.status === 200) {
        let data = [];
        if (res.resultObject) {
          const parsed = typeof res.resultObject === 'string' && res.resultObject !== '' ? JSON.parse(res.resultObject) : res.resultObject;
          data = parsed;
          setTotalCount(Number(res.message.split(" ")[1]));
        }
        setPlansData(data);
      } else {
        showToaster(res.message, 'error');
        setTimeout(() => {
          hideToaster();
        }, Constants.TOASTERHIDETIME);
      }
    } catch (error) {
      console.log(error);
      showToaster('Failed to fetch subscription plans', 'error');
      setTimeout(() => {
        hideToaster();
      }, Constants.TOASTERHIDETIME);
    } finally {
      setTimeout(() => {
        hideLoader();
      }, 0);
    }
  }

  async function activateDeactivatePlan(planId, isActive) {
    try {
      showLoader();
      const res = await toggleSubscriptionPlanStatus(planId, isActive, 'active');
      if (res.status === 200) {
        showToaster(res.message, 'success');
        fetchSubscriptionPlans(page, rowsPerPage, searchText);
        setTimeout(() => {
          hideToaster();
        }, Constants.TOASTERHIDETIME);
      }
      else {
        showToaster(res.message, 'error');
        fetchSubscriptionPlans(page, rowsPerPage, searchText);
        setTimeout(() => {
          hideToaster();
        }, Constants.TOASTERHIDETIME);
      }
    }
    catch (error) {
      console.log(error);
      showToaster('Failed to ' + (isActive ? 'activate' : 'deactivate') + ' plan', 'error');
      setTimeout(() => {
        hideToaster();
      }, Constants.TOASTERHIDETIME);
    } finally {
      hideLoader();
    }
  }

  async function deletePlan(planId) {
    try {
      showLoader();
      const res = await toggleSubscriptionPlanStatus(planId, 0, 'delete');
      if (res.status === 200) {
        showToaster(res.message, 'success');
        fetchSubscriptionPlans(page, rowsPerPage, searchText);
        setTimeout(() => {
          hideToaster();
        }, Constants.TOASTERHIDETIME);
      }
      else {
        showToaster(res.message, 'error');
        fetchSubscriptionPlans(page, rowsPerPage, searchText);
        setTimeout(() => {
          hideToaster();
        }, Constants.TOASTERHIDETIME);
      }
    }
    catch (error) {
      console.log(error);
      showToaster('Failed to delete plan', 'error');
      setTimeout(() => {
        hideToaster();
      }, Constants.TOASTERHIDETIME);
    } finally {
      hideLoader();
    }
  }

  return (
    <>
      <SideNavigation />

      <Header
        title="Subscription Plans"
      />

      <div className="dashboard-wrap application-detail">
        <div className="p-3">
          <div className="d-flex justify-content-between pt-3">
            <div className="lm-search-bar">
              <input
                style={{ width: '300px' }}
                type="text"
                id="searchInput"
                placeholder="Search by number, name, or other"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') setPage(1); }}
              />
            </div>
            <a onClick={() => { setPlanId(0); setShowAddPlanModal(true); }} className="lm-button3">
              <span>+</span> Add a new plan
            </a>
          </div>
          <DataTable
            isLoading={isLoading}
            tableId="loanAppTable"
            columns={[
              // { key: 'RowNumber', label: '#', sortable: true },
              { key: 'PlanName', label: 'Plan Name', sortable: true },
              {
                key: 'Price',
                label: 'Price',
                sortable: true,
                render: (value, row) => (
                  <span>${formatDollars(value)}</span>
                ),
              },
              {
                key: 'CreatedDate',
                label: 'Created Date',
                sortable: true,
                render: (value, row) => (
                  <span>{value ? moment(value).format('MMM DD, YYYY') : '-'}</span>
                ),
              },
              {
                key: 'Status',
                label: 'Status',
                sortable: true,
                render: (value, row) => {
                  const isActive = getBoolean(value ?? row.Status);
                  return (
                    <div className="lm-toggle-switch">
                      <input
                        type="checkbox"
                        id={`switch-${row.Id}`}
                        checked={isActive}
                        onChange={() => {
                          setStatusConfirmPlan({
                            id: row.Id,
                            planName: row.PlanName,
                            newIsActive: !isActive,
                          });
                          setShowStatusConfirmPopup(true);
                        }}
                      />
                      <label htmlFor={`switch-${row.Id}`}>Status</label>
                    </div>
                  );
                },
              },
              {
                key: 'action',
                label: 'Action',
                sortable: false,
                cellClassName: 'action-col',
                render: (_, row) => (
                  <div>
                    <div className="options_btn">
                      <span className="option_dots">...</span>
                      <div id="popoverContent" className="app_options">
                        <div
                          onClick={() => {
                            setPlanId(row.Id);
                            setShowAddPlanModal(true);
                          }}
                        >
                          <img className="me-2" src={editPenIcon} alt="" /> Edit
                        </div>
                        <div
                          onClick={() => {
                            setStatusConfirmPlan({
                              id: row.Id,
                              planName: row.PlanName,
                              newIsActive: 0,
                            });
                            setShowDeletePopup(true);
                          }}
                        >
                          <img className="me-2" src={trashGridIcon} alt="" /> Delete
                        </div>
                      </div>
                    </div>
                  </div>
                ),
              },
            ]}
            data={plansData}
            defaultRowsPerPage={10}
            totalCount={totalCount}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={setPage}
            onRowsPerPageChange={(val) => { setRowsPerPage(val); setPage(1); }}
          />
        </div>
      </div>

      {
        showAddPlanModal && (
          <AddPlanDrawer
            show={showAddPlanModal}
            showAnimation={showAddPlanModalAnimation}
            onClose={() => {
              setShowAddPlanModal(false);
              setShowAddPlanModalAnimation(false);
              setPlanId(0);
            }}
            planId={planId}
            onPlanAdded={() => {
              setShowAddPlanModal(false);
              setShowAddPlanModalAnimation(false);
              setPlanId(0);
              fetchSubscriptionPlans(page, rowsPerPage, searchText);
            }}
          />
        )
      }

      {showStatusConfirmPopup && statusConfirmPlan && (
        <>
          <div
            className="modal-backdrop fade show"
            onClick={() => { setShowStatusConfirmPopup(false); setStatusConfirmPlan(null); }}
          ></div>
          <div className="modal show d-block fade" id="statusConfirmPopup" tabIndex="-1" role="dialog" aria-labelledby="statusConfirmPopupLabel" aria-hidden="false">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-body">
                  <h6 className="pt-4">
                    Are you sure you want to {statusConfirmPlan.newIsActive ? 'activate' : 'deactivate'} plan &quot;{statusConfirmPlan.planName || 'this plan'}&quot;?
                  </h6>
                </div>
                <div className="modal-footer">
                  <button type="button" className="lm-button2" data-dismiss="modal" onClick={() => { setShowStatusConfirmPopup(false); setStatusConfirmPlan(null); }}>Cancel</button>
                  <button
                    type="button"
                    className="lm-button1"
                    onClick={() => {
                      activateDeactivatePlan(statusConfirmPlan.id, statusConfirmPlan.newIsActive);
                      setShowStatusConfirmPopup(false);
                      setStatusConfirmPlan(null);
                    }}
                  >
                    Confirm
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {showDeletePopup && statusConfirmPlan && (
        <>
          <div
            className="modal-backdrop fade show"
            onClick={() => { setShowDeletePopup(false); setStatusConfirmPlan(null); }}
          ></div>
          <div className="modal show d-block fade" id="deletePopup" tabIndex="-1" role="dialog" aria-labelledby="deletePopupLabel"
            aria-hidden="false">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-body">
                  <h6 className="pt-4">Are you sure you want to delete plan &quot;{statusConfirmPlan.planName || 'this plan'}&quot;?</h6>
                </div>
                <div className="modal-footer border-0">
                  <button type="button" className="lm-button2" data-dismiss="modal" onClick={() => { setShowDeletePopup(false); setStatusConfirmPlan(null); }}>Cancel</button>
                  <button type="button" className="lm-button1" onClick={() => { deletePlan(statusConfirmPlan.id); setShowDeletePopup(false); setStatusConfirmPlan(null); }}>Confirm</button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Plans;
